import React from "react"
import { FormattedMessage } from "react-intl"
import { WingsButton, WingsDetailText, WingsText } from "@wingspans-ui"
import { navigate } from "../../../link"

const STEP_ZONE_COPY = {
  assessment: (
    <FormattedMessage
      id="roadmap-step-assessment-description"
      defaultMessage="Take the first step to finding a career that aligns with who you are. Choose either a 6 or 60-question interest profiler, which outputs information on your personality and career matches, and introduces you to inspiring people along the way."
    />
  ),
  skills: (
    <FormattedMessage
      id="roadmap-step-skills-description"
      defaultMessage="Meet TAL-E, an interactive tool that identifies your skills from your lived experiences, weaves them into a cohesive story, and matches you with potential careers."
    />
  ),
  explore: (
    <FormattedMessage
      id="roadmap-step-explore-description"
      defaultMessage="Expand your idea of what is possible. Select More Filters to sort stories and careers by identity, personality, interests, and credentials."
    />
  ),
  "soft-skills": (
    <FormattedMessage
      id="roadmap-step-soft-skills-description"
      defaultMessage="Take an active role in your future. Hone your communication skills, learn time management, and chart an inspiring path for your future."
    />
  ),
  resume: (
    <FormattedMessage
      id="roadmap-step-resume-description"
      defaultMessage="Personalize a resume that tells the story of who you are. Bookmark pages that inspire you, assess your interests and skills and build a resume that exports to a beautiful, ATS-styled Word doc."
    />
  ),
  "career-plan": (
    <FormattedMessage
      id="roadmap-step-career-plan-description"
      defaultMessage="Go beyond imagining and plan your path! Create a personalized timeline using our Guided or Unguided workflow. Dare to dream big."
    />
  ),
  goals: (
    <FormattedMessage
      id="roadmap-step-goals-description"
      defaultMessage="Record the specific, actionable goals you're working on right now and something you're looking forward to. It's a simple but powerful way to keep your momentum going."
    />
  ),
}

/**
 * Renders the informational text shown inside a roadmap step dropdown.
 *
 * @param {Object} props
 * @param {string|number} props.minutes - Estimated duration in minutes.
 * @param {string|undefined} props.dueDate - Optional due date text.
 * @returns {JSX.Element}
 */
export const RoadmapStepSummary = ({ minutes, dueDate }) => (
  <div className="step-zone w-mx-2">
    <WingsText className="roadmap-step-description w-mb-1">
      {dueDate && (
        <>
          <FormattedMessage id="roadmap-due" defaultMessage="Due" />{" "}
          {dueDate}
          {" | "}
        </>
      )}
      {minutes} <FormattedMessage id="roadmap-min" defaultMessage="Min" />
    </WingsText>
  </div>
)

/**
 * Renders the supporting description copy shown in the roadmap step dropdown.
 *
 * @param {Object} props
 * @param {string} props.stepKey - Identifier for the roadmap step.
 * @param {boolean} props.isActive - Whether this step is the current step.
 * @returns {JSX.Element}
 */
export const RoadmapStepDescription = ({ stepKey, isActive }) => (
  <WingsDetailText as="div" className="roadmap-step-meta px-4">
    {STEP_ZONE_COPY[stepKey] ?? (
      <FormattedMessage
        id={isActive ? "roadmap-step-active-description" : "roadmap-step-description"}
        defaultMessage={isActive ? "This is your current step." : "Expand this step to review details."}
      />
    )}
  </WingsDetailText>
)

/**
 * Renders a CTA button for opening the page associated with a roadmap step.
 *
 * @param {Object} props
 * @param {string} props.href - Destination page for the roadmap step.
 * @param {string} props.labelId - Translation id for the roadmap step button label.
 * @param {string} props.defaultLabel - Fallback label for the roadmap step button.
 * @returns {JSX.Element|null}
 */
export const RoadmapStepButton = ({ href, labelId, defaultLabel }) => {
  if (!href) {
    return null
  }

  return (
    <WingsButton
      variant="primary"
      size="mini"
      className="w-mt-1 w-mx-1"
      onClick={() => navigate(href)}
    >
      <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
    </WingsButton>
  )
}

export default RoadmapStepSummary